import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ChevronRight,
  Link2,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useBusinesses } from '@/features/business/use-businesses';
import { formatCurrency, formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AssociateSheet, ItemRequestSheet } from './connect-sheets';
import {
  SEARCH_BY,
  priceMethodLabelKey,
  taxRuleLabelKey,
  useApplyPrice,
  useCheckForMatches,
  usePriceSearch,
  type ItemPrice,
  type PotentialMatch,
  type PriceSearchModel,
} from './item-prices.api';

/**
 * Item prices — what the POS says an item costs.
 *
 * A row is either already tied to a catalogue item, in which case its price can
 * be pushed to the storefront, or it is not, in which case it has to be
 * connected first. Which of the two it is decides what the row offers, so it is
 * stated on the row rather than left to a button that fails.
 */

const ANY = 'ANY';

/** One labelled figure in the expanded detail. */
function Detail({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">{children}</span>
    </div>
  );
}

function DetailGroup({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
        {title}
      </p>
      {children}
    </div>
  );
}

export function ItemPricesPage() {
  const { t } = useTranslation();

  const { data: businesses } = useBusinesses();
  const [businessId, setBusinessId] = useState(ANY);
  const [searchBy, setSearchBy] = useState(String(SEARCH_BY.general));
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const [matchesFor, setMatchesFor] = useState<{
    price: ItemPrice;
    items: PotentialMatch[];
  } | null>(null);
  const [requesting, setRequesting] = useState<ItemPrice | null>(null);
  const [associating, setAssociating] = useState<{
    price: ItemPrice;
    target: { itemId: string | null; itemRequestId: string | null; label: string };
  } | null>(null);

  const store = businessId === ANY ? null : businessId;
  const by = Number(searchBy);

  const model: PriceSearchModel = useMemo(
    () => ({
      // Only the field the search is by carries the text; the API reads the
      // other two as "no constraint" rather than "empty string".
      Query: by === SEARCH_BY.general ? query : '',
      Gtin: by === SEARCH_BY.gtin ? query : '',
      ItemNumber: by === SEARCH_BY.itemNumber ? query : '',
      Page: page,
      Size: size,
      BusinessId: store,
    }),
    [by, query, page, size, store]
  );

  const search = usePriceSearch(model);
  const apply = useApplyPrice();
  const check = useCheckForMatches();

  const rows = search.data?.items ?? [];
  const total = search.data?.totalItems ?? 0;

  const placeholder = {
    [SEARCH_BY.general]: t('itemPrices.searchGeneral'),
    [SEARCH_BY.gtin]: t('itemPrices.searchGtin'),
    [SEARCH_BY.itemNumber]: t('itemPrices.searchItemNumber'),
  }[by];

  function updatePrice(price: ItemPrice) {
    apply.mutate(
      { upc: price.upc ?? '', businessId: store },
      {
        onSuccess: () => toast.success(t('itemPrices.priceApplied')),
        onError: () => toast.error(t('itemPrices.actionFailed')),
      }
    );
  }

  /** Ask what this price might already be before offering to create anything. */
  function connect(price: ItemPrice) {
    check.mutate(price.id, {
      onSuccess: (data) =>
        setMatchesFor({ price, items: data.items ?? [] }),
      onError: () => toast.error(t('itemPrices.actionFailed')),
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('itemPrices.title')}
        description={t('itemPrices.subtitle')}
        actions={
          <Button
            variant="outline"
            size="icon-sm"
            aria-label={t('itemPrices.reload')}
            disabled={search.isFetching}
            onClick={() => void search.refetch()}
          >
            <RefreshCw className={search.isFetching ? 'animate-spin' : ''} />
          </Button>
        }
      />

      <div className="space-y-2">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Select
            value={businessId}
            disabled={search.isFetching}
            onValueChange={(value) => {
              setBusinessId(value);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-10 sm:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>{t('itemPrices.anyStore')}</SelectItem>
              {(businesses ?? []).map((business) => (
                <SelectItem key={business.id} value={business.id}>
                  {business.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={searchBy}
            disabled={search.isFetching}
            onValueChange={(value) => {
              setSearchBy(value);
              setPage(0);
            }}
          >
            <SelectTrigger className="h-10 sm:w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={String(SEARCH_BY.general)}>
                {t('itemPrices.byGeneral')}
              </SelectItem>
              <SelectItem value={String(SEARCH_BY.gtin)}>GTIN</SelectItem>
              <SelectItem value={String(SEARCH_BY.itemNumber)}>
                {t('itemPrices.byItemNumber')}
              </SelectItem>
            </SelectContent>
          </Select>

          <form
            role="search"
            className="flex flex-1 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
                className="h-10 pl-9"
              />
            </div>
            <Button
              type="submit"
              className="h-10"
              disabled={search.isFetching}
            >
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        </div>

        {search.isSuccess && (
          <p className="text-muted-foreground text-sm">
            {t('itemPrices.showingOf', { shown: rows.length, total })}
          </p>
        )}

        {/* Connecting is per store, so the control that decides it says so. */}
        {!store && (
          <p className="text-muted-foreground text-xs">
            {t('itemPrices.pickStoreToConnect')}
          </p>
        )}
      </div>

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('itemPrices.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void search.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="m-3 h-12" />
          ))}
        </div>
      )}

      {search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('itemPrices.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="space-y-3">
          <div className="relative rounded-xl">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                'divide-y overflow-hidden rounded-xl border',
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {rows.map((price) => (
                <PriceRow
                  key={price.id}
                  price={price}
                  store={store}
                  busy={apply.isPending || check.isPending}
                  onApply={() => updatePrice(price)}
                  onConnect={() => connect(price)}
                />
              ))}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      {/* ------------------------------------------- potential matches */}
      <AlertDialog
        open={matchesFor !== null}
        onOpenChange={(next) => {
          if (!next) setMatchesFor(null);
        }}
      >
        <AlertDialogContent className="max-h-[85vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('itemPrices.foundItems')}</AlertDialogTitle>
            <AlertDialogDescription>
              {matchesFor?.items.length
                ? t('itemPrices.foundPotentialMatches')
                : t('itemPrices.noPotentialMatch')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {(matchesFor?.items ?? []).length > 0 && (
            <div className="divide-y rounded-xl border">
              {matchesFor!.items.map((match) => (
                <div key={match.id} className="flex items-center gap-3 p-3">
                  {match.itemImage ? (
                    <img
                      src={match.itemImage}
                      alt=""
                      className="bg-muted size-12 shrink-0 rounded object-contain"
                    />
                  ) : (
                    <span className="bg-muted size-12 shrink-0 rounded" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">
                      {[match.brand, match.name].filter(Boolean).join(' ')}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {match.description}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setAssociating({
                        price: matchesFor!.price,
                        target: {
                          itemId: match.itemId ?? match.id,
                          itemRequestId: match.itemRequestId ?? null,
                          label: [match.brand, match.name]
                            .filter(Boolean)
                            .join(' '),
                        },
                      });
                      setMatchesFor(null);
                    }}
                  >
                    {t('itemPrices.associate')}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMatchesFor(null)}>
              {t('itemPrices.close')}
            </Button>
            <Button
              onClick={() => {
                setRequesting(matchesFor!.price);
                setMatchesFor(null);
              }}
            >
              {t('itemPrices.createNew')}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <ItemRequestSheet
        price={requesting}
        businessId={store}
        open={requesting !== null}
        onOpenChange={(next) => {
          if (!next) setRequesting(null);
        }}
        onSaved={() => void search.refetch()}
      />

      <AssociateSheet
        price={associating?.price ?? null}
        target={associating?.target ?? null}
        businessId={store}
        open={associating !== null}
        onOpenChange={(next) => {
          if (!next) setAssociating(null);
        }}
        onSaved={() => void search.refetch()}
      />
    </div>
  );
}

/** One price, opening to everything the POS knows about it. */
function PriceRow({
  price,
  store,
  busy,
  onApply,
  onConnect,
}: {
  price: ItemPrice;
  store: string | null;
  busy: boolean;
  onApply: () => void;
  onConnect: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const yesNo = (value: boolean) =>
    value ? t('common.yes') : t('common.no');

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center">
        <CollapsibleTrigger className="hover:bg-accent/40 -m-1 flex min-w-0 flex-1 items-center gap-2 rounded-lg p-1 text-left transition-colors">
          <ChevronRight
            className={cn(
              'text-muted-foreground size-4 shrink-0 transition-transform',
              open && 'rotate-90'
            )}
          />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">
              {[price.brand, price.name, price.description]
                .filter(Boolean)
                .join(' ')}
            </p>
            <p className="text-muted-foreground truncate font-mono text-xs">
              {[price.itemNumber, price.gtin].filter(Boolean).join(' · ')}
            </p>
          </div>
        </CollapsibleTrigger>

        <div className="text-right sm:w-32">
          <p className="font-semibold tabular-nums">
            {formatCurrency(price.basePrice)}
          </p>
          {(price.promoPrice ?? 0) > 0 && (
            <p className="text-success text-xs tabular-nums">
              {formatCurrency(price.promoPrice)} {t('itemPrices.promo')}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {store &&
            (price.isConnected ? (
              <Badge variant="success">{t('itemPrices.connected')}</Badge>
            ) : (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onConnect}
              >
                {busy ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Link2 className="size-3.5" />
                )}
                {t('itemPrices.connect')}
              </Button>
            ))}
          <Button size="sm" disabled={busy} onClick={onApply}>
            {t('itemPrices.updatePrice')}
          </Button>
        </div>
      </div>

      <CollapsibleContent>
        <div className="bg-muted/40 grid gap-4 border-t p-3 sm:grid-cols-2 lg:grid-cols-4">
          <DetailGroup title={t('itemPrices.details')}>
            <Detail label={t('itemPrices.itemNumber')}>
              {price.itemNumber || '—'}
            </Detail>
            <Detail label="GTIN">{price.gtin || '—'}</Detail>
            <Detail label={t('itemPrices.internalName')}>
              {price.internalName || '—'}
            </Detail>
            <Detail label={t('itemPrices.department')}>
              {price.department || '—'}
            </Detail>
            <Detail label={t('itemPrices.aisle')}>{price.aisle || '—'}</Detail>
            <Detail label={t('itemPrices.quantityOnHand')}>
              {price.quantityOnHand != null
                ? formatNumber(price.quantityOnHand)
                : '—'}
            </Detail>
          </DetailGroup>

          <DetailGroup title={t('itemPrices.pricing')}>
            <Detail label={t('itemPrices.taxRuleLabel')}>
              {t(taxRuleLabelKey(price.taxRule))}
            </Detail>
            <Detail label={t('itemPrices.basePrice')}>
              {formatCurrency(price.basePrice)}
            </Detail>
            <Detail label={t('itemPrices.baseMultiple')}>
              {formatNumber(price.baseMultiple)}
            </Detail>
            <Detail label={t('itemPrices.basePriceMethod')}>
              {t(priceMethodLabelKey(price.basePriceMethod))}
            </Detail>
            <Detail label={t('itemPrices.cost')}>
              {formatCurrency(price.cost)}
            </Detail>
            <Detail label={t('itemPrices.pack')}>
              {formatNumber(price.pack)}
            </Detail>
            <Detail label={`${t('itemPrices.stateTax')} %`}>
              {formatNumber(price.stateTaxPct)}
            </Detail>
            <Detail label={`${t('itemPrices.municipalTax')} %`}>
              {formatNumber(price.munTaxPct)}
            </Detail>
          </DetailGroup>

          <DetailGroup title={t('itemPrices.promo')}>
            <Detail label={t('itemPrices.promoPrice')}>
              {formatCurrency(price.promoPrice)}
            </Detail>
            <Detail label={t('itemPrices.promoMultiple')}>
              {formatNumber(price.promoMultiple)}
            </Detail>
            <Detail label={t('itemPrices.promoPriceMethod')}>
              {t(priceMethodLabelKey(price.promoPriceMethod))}
            </Detail>
            <Detail label={t('itemPrices.promoStartDate')}>
              {price.promoStartDate ? formatDateTime(price.promoStartDate) : '—'}
            </Detail>
            <Detail label={t('itemPrices.promoEndDate')}>
              {price.promoEndDate ? formatDateTime(price.promoEndDate) : '—'}
            </Detail>
          </DetailGroup>

          <DetailGroup title={t('itemPrices.flags')}>
            <Detail label="EBT">{yesNo(price.isEbt)}</Detail>
            <Detail label={t('itemPrices.idRequired')}>
              {yesNo(price.idRequired)}
            </Detail>
            <Detail label={t('itemPrices.alcoholicBeverage')}>
              {yesNo(price.isAlcoholicBeverage)}
            </Detail>
            <Detail label={t('itemPrices.isVisible')}>
              {yesNo(price.isVisible)}
            </Detail>
            <Detail label={t('itemPrices.isDeleted')}>
              {yesNo(price.isDeleted)}
            </Detail>
            <Detail label={t('itemPrices.editedOn')}>
              {formatDateTime(price.editedAt)}
            </Detail>
            <Detail label={t('itemPrices.createdOn')}>
              {formatDateTime(price.createdAt)}
            </Detail>
          </DetailGroup>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
